<template>
  <div class="pot-backdrop" @click.self="onClose">
    <div class="pot-modal" role="dialog" aria-modal="true" aria-label="Present on Tablet">
      <header class="pot-header">
        <div>
          <div class="pot-eyebrow">Present on Tablet</div>
          <div class="pot-title">{{ patientName || 'Treatment plan' }}</div>
          <div class="pot-sub">
            <span v-if="patientDob">DOB {{ patientDob }}</span>
            <span v-if="patientDob && (planHeading || planTotal)" class="pot-dot">·</span>
            <span v-if="planHeading">{{ planHeading }}</span>
            <span v-if="planTotal" class="pot-total">{{ planTotal }}</span>
          </div>
        </div>
        <button class="pot-close" @click="onClose" aria-label="Close">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="pot-body">
        <div class="pot-qr-wrap">
          <canvas ref="qr" class="pot-qr"></canvas>
        </div>
        <p class="pot-hint">
          Scan this code with your tablet or phone camera to open this plan in
          <strong>Present Mode</strong>. You'll need to be signed in to Teamio on that device.
        </p>

        <div class="pot-link-row">
          <input class="pot-link-input" type="text" :value="presentUrl" readonly @focus="$event.target.select()" />
          <button class="pot-copy-btn" @click="copyLink">
            <i class="fa" :class="copied ? 'fa-check' : 'fa-clipboard'"></i>
            {{ copied ? 'Copied' : 'Copy link' }}
          </button>
        </div>
      </div>

      <footer class="pot-footer">
        <button class="pot-btn" @click="onClose">Close</button>
      </footer>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';

export default {
  name: 'PresentOnTabletModal',
  props: {
    patNum: { type: [Number, String], required: true },
    treatPlanNum: { type: [Number, String], required: true },
    patientName: { type: String, default: '' },
    patientDob: { type: String, default: '' },
    planHeading: { type: String, default: '' },
    planTotal: { type: String, default: '' },
  },
  data() {
    return { copied: false };
  },
  computed: {
    // Internal authenticated route — encode the live origin so dev encodes dev
    // and prod encodes prod automatically. No PHI in the URL (opaque IDs only).
    presentUrl() {
      return `${window.location.origin}/app/tx-presenter/present/${this.patNum}/${this.treatPlanNum}`;
    },
  },
  mounted() {
    this.$nextTick(this.renderQR);
  },
  methods: {
    renderQR() {
      const canvas = this.$refs.qr;
      if (!canvas) return;
      QRCode.toCanvas(canvas, this.presentUrl, { width: 240, margin: 1 }, (err) => {
        if (err) console.error('[PresentOnTablet] QR render error', err);
      });
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.presentUrl);
        this.copied = true;
        if (this.$toast) this.$toast.success('Link copied to clipboard!');
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        console.error('[PresentOnTablet] clipboard copy failed', err);
        if (this.$toast) this.$toast.error('Failed to copy link');
      }
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.pot-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200; /* above the Treatment Plans popover (z-index 100) + drawer */
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.pot-modal {
  background: #fff;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pot-modal * { box-sizing: border-box; }

.pot-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #e4e7ec;
}
.pot-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}
.pot-title {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
  margin-top: 4px;
}
.pot-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 12.5px;
  color: #64748b;
  margin-top: 4px;
}
.pot-dot { color: #cbd5e1; }
.pot-total { font-weight: 600; color: #0d5f54; }
.pot-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;
}
.pot-close:hover { background: #f1f3f6; }

.pot-body {
  padding: 20px 22px 4px;
  overflow-y: auto;
}
.pot-qr-wrap {
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #f7f8fa;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
}
.pot-qr { width: 240px; height: 240px; display: block; }
.pot-hint {
  font-size: 12.5px;
  color: #475569;
  line-height: 1.5;
  text-align: center;
  margin: 14px 4px 16px;
}
.pot-hint strong { color: #0f172a; }

.pot-link-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.pot-link-input {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  font-size: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  color: #475569;
  background: #f7f8fa;
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
}
.pot-link-input:focus { outline: none; border-color: #0d5f54; }
.pot-copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #b9dcd5;
  background: #fff;
  color: #0d5f54;
  white-space: nowrap;
}
.pot-copy-btn:hover { background: #ecf5f3; }
.pot-copy-btn .fa { font-size: 11px; }

.pot-footer {
  padding: 14px 22px;
  border-top: 1px solid #e4e7ec;
  display: flex;
  justify-content: flex-end;
  background: #fafafa;
  margin-top: 16px;
}
.pot-btn {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.pot-btn:hover { background: #f1f3f6; }
</style>
